import React, { useEffect, useState } from 'react';
import { Field } from 'formik';
import { useFormikContext } from 'formik';

import { InputUI, AutoCompleteSelectUI, AutoCompleteSelectServiceUI, FormErrorUI, FormLabelUI } from '../Interface';
import { multiply } from 'mathjs';
import useParam from '../../context/ParamContext';
import warehouseService from '../../service/warehouse.service';
import { generateAutocompleteItems } from '../../utils/helper';
import masterDataService from '../../service/masterData.service';
import { itemAutocompleteRequestBuilder, itemAutocompleteService } from '../../utils/itemAutocomplete';

const SourceListFields = ({
  viewField,
  selectedValues,
  autocompleteData,
  autocompleteOptions,
  handleImportTemplateItem,
}) => {
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const { getParam, params } = useParam();
  const [binOptions, setBinOptions] = useState([]);

  const normalizeSingleItemResponse = (response) => {
    if (Array.isArray(response?.body)) {
      return response.body[0] ?? null;
    }

    if (Array.isArray(response)) {
      return response[0] ?? null;
    }

    return response?.body ?? response ?? null;
  };

  const getItemMetaByCode = async (itemCode) => {
    if (!itemCode) {
      return null;
    }

    const currentFormItem = values?.item;
    if (currentFormItem?.itemCode === itemCode) {
      return currentFormItem;
    }

    const selectedItem = selectedValues?.item;
    if (selectedItem?.itemCode === itemCode) {
      return selectedItem;
    }

    try {
      const response = await masterDataService.itemsFromTableToFormByItemCode(itemCode);
      return normalizeSingleItemResponse(response);
    } catch {
      return null;
    }
  };

  useEffect(() => {
    getParam();
  }, []);

  useEffect(() => {
    if (values.whsCode) {
      warehouseService
        .getBins(values.whsCode)
        .then((data) => {
          setBinOptions(generateAutocompleteItems(data ?? [], 'binCode', 'binId'));
        })
        .catch(() => {
          setBinOptions([]);
        });
    } else {
      setBinOptions([]);
      setFieldValue('binId', null);
    }
  }, [values.whsCode]);

  return (
    <div className="grid grid-cols-4 gap-x-4 mx-4 mb-5">
      <div className="col-span-4 ">
        <Field
          type="autocomplete"
          name="itemCode"
          component={AutoCompleteSelectServiceUI}
          fullWidth
          label={<FormLabelUI text="Cikk" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.itemCode} />}
          service={itemAutocompleteService}
          serviceRequestBuilder={itemAutocompleteRequestBuilder}
          onChange={async (_e, newVal) => {
            const selectedItemCode = newVal?.value ?? null;
            await setFieldValue('itemCode', selectedItemCode);
            await validateField('itemCode');

            const selectedItem = await getItemMetaByCode(selectedItemCode);
            setFieldValue('item', selectedItem ?? null);
            setFieldValue('quantityUnitId', selectedItem?.quantityUnitId ?? null);
            setFieldValue('weightPerItem', selectedItem?.weight ?? 0);
            setFieldValue('weight', multiply(selectedItem?.weight ?? 0, values.baseQuantity ?? 0));
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.itemCode !== '' }}
          selectedValue={values.itemCode}
          selectedLabelValue={
            [
              values.itemCode ?? selectedValues?.itemCode,
              values?.item?.itemName ?? values?.item?.name ?? selectedValues?.item?.itemName ?? selectedValues?.item?.name,
            ]
              .filter(Boolean)
              .join(' - ') ||
            values.itemCode ||
            selectedValues?.itemCode ||
            ''
          }
        />
      </div>
      <div className="md:col-span-1 col-span-4">
        <Field
          type="number"
          name="baseQuantity"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Mennyiség" />}
          helperText={<FormErrorUI message={errors.baseQuantity} />}
          variant="standard"
          disabled={viewField}
          InputLabelProps={{ shrink: values.baseQuantity && values.baseQuantity !== '' }}
          onChange={async (e) => {
            const value = parseFloat(e.target.value);
            setFieldValue('baseQuantity', value);
            await validateField('baseQuantity');
            const selectedItem = await getItemMetaByCode(values.itemCode);
            setFieldValue('weight', multiply(selectedItem?.weight ?? 0, value));
            setFieldValue('weightPerItem', selectedItem?.weight ?? 0);
          }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          disabled
          name="quantityUnitId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Mennyiségi egység" />}
          variant="standard"
          InputLabelProps={{ shrink: values.quantityUnitId !== '' }}
          selectedValue={values.quantityUnitId}
          selectedLabelValue={selectedValues?.item?.name}
          dataset={autocompleteOptions?.quantityUnit}
        />
      </div>
      <div className="col-span-4 md:col-span-2">
        <Field
          type="autocomplete"
          name="templateItem"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Sabloncikk" />}
          variant="standard"
          onChange={(_e, newVal) => {
            setFieldValue('templateItem', newVal?.value ?? null);
            handleImportTemplateItem(newVal?.value, values, setFieldValue);
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.itemCode !== '' }}
          selectedValue={values.templateItem}
          dataset={autocompleteOptions?.templateItems}
        />
      </div>
      {params?.ENABLE_VARIANT === 'Y' && (
        <>
          <div className="col-span-4 ">
            <Field
              type="autocomplete"
              name="variantMainItemCode"
              component={AutoCompleteSelectServiceUI}
              fullWidth
              label={<FormLabelUI text="Variáns cikk" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.itemCode} />}
              service={itemAutocompleteService}
              serviceRequestBuilder={itemAutocompleteRequestBuilder}
              onChange={(_e, newVal) => {
                setFieldValue('variantMainItemCode', newVal?.value ?? null).then(() => {
                  validateField('variantMainItemCode');
                });
              }}
              disabled={viewField}
              InputLabelProps={{ shrink: values.variantMainItemCode !== '' }}
              selectedValue={values.variantMainItemCode}
              selectedLabelValue={
                [values.variantMainItemCode, selectedValues?.variantMainItemCode?.itemName]
                  .filter(Boolean)
                  .join(' - ') ||
                values.variantMainItemCode ||
                ''
              }
            />
          </div>
          <div className="col-span-4 ">
            <Field
              type="text"
              name="variantNote"
              as={InputUI}
              fullWidth
              label={<FormLabelUI text="Variáns leírás" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.variantNote} />}
              disabled={viewField}
              InputLabelProps={{ shrink: values.variantNote && values.variantNote !== '' }}
            />
          </div>
        </>
      )}
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="whsCode"
          component={AutoCompleteSelectUI}
          fullWidth
          disabled={viewField}
          label={<FormLabelUI text="Raktár" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.whsCode} />}
          InputLabelProps={{ shrink: Boolean(values.whsCode) }}
          onChange={(_e, newVal) => {
            setFieldValue('whsCode', newVal?.value ?? null).then(() => {
              validateField('whsCode');
            });
          }}
          selectedValue={values.whsCode}
          selectedLabelValue={autocompleteData?.warehouse?.find((p) => p.code === values.whsCode)?.code}
          dataset={autocompleteOptions?.warehouse}
        />
      </div>
      {binOptions.length > 0 && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="binId"
            component={AutoCompleteSelectUI}
            fullWidth
            disabled={viewField}
            label={<FormLabelUI text="Rakhely" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.binId} />}
            InputLabelProps={{ shrink: Boolean(values.binId) }}
            onChange={(_e, newVal) => {
              setFieldValue('binId', newVal?.value ?? null).then(() => {
                validateField('binId');
              });
            }}
            selectedValue={values.binId}
            selectedLabelValue={binOptions.find((b) => b.value === values.binId)?.label ?? ''}
            dataset={binOptions}
          />
        </div>
      )}
      <div className="col-span-4 md:col-span-1">
        <Field
          type="number"
          name="weight"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Súly" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.weight} />}
          disabled
          InputLabelProps={{ shrink: values.weight !== '' && values.weight !== null }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="number"
          name="weightPerItem"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Súly / Tétel" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.weightPerItem} />}
          disabled
          InputLabelProps={{ shrink: values.weightPerItem !== '' && values.weightPerItem !== null }}
        />
      </div>
    </div>
  );
};

export default SourceListFields;
